/**
 * mui-theme.ts - Material UI Theme Configuration for Skinnr
 * 
 * CHANGE HISTORY:
 * - 251025_1728 danielbeer: Initial MUI theme integration with Skinnr
 * 
 * Maps Skinnr CSS variables to Material UI theme system for consistent theming
 */

import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

/**
 * Theme mode type
 */
export type SkinnrThemeMode = 'light' | 'dark' | 'high-contrast';

/**
 * Get CSS variable value from document root
 */
const getCSSVariable = (variable: string): string => {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
};

/**
 * Create Material UI theme from Skinnr CSS variables
 * 
 * This bridges Skinnr's CSS variable system with MUI's theme system,
 * ensuring consistent styling across all OpCentral applications.
 */
export const createSkinnrMuiTheme = (mode: SkinnrThemeMode = 'light'): Theme => {
  const themeOptions: ThemeOptions = {
    palette: {
      mode: mode === 'high-contrast' ? 'light' : mode,
      primary: {
        main: getCSSVariable('--color-primary') || '#3b82f6',
        light: getCSSVariable('--color-primary-hover') || '#60a5fa',
        dark: getCSSVariable('--color-primary-active') || '#2563eb',
        contrastText: getCSSVariable('--button-primary-text') || '#ffffff',
      },
      secondary: {
        main: getCSSVariable('--color-secondary') || '#6b7280',
        light: getCSSVariable('--color-secondary-hover') || '#9ca3af',
        dark: getCSSVariable('--color-secondary-active') || '#4b5563',
        contrastText: getCSSVariable('--text-primary') || '#111827',
      },
      error: {
        main: getCSSVariable('--color-error') || '#ef4444',
        light: getCSSVariable('--color-error-hover') || '#f87171',
        dark: '#b91c1c',
        contrastText: '#ffffff',
      },
      warning: {
        main: getCSSVariable('--color-warning') || '#f59e0b',
        light: getCSSVariable('--color-warning-hover') || '#fbbf24',
        dark: '#d97706',
        contrastText: '#ffffff',
      },
      info: {
        main: getCSSVariable('--color-info') || '#3b82f6',
        light: getCSSVariable('--color-info-hover') || '#60a5fa',
        dark: '#2563eb',
        contrastText: '#ffffff',
      },
      success: {
        main: getCSSVariable('--color-success') || '#10b981',
        light: getCSSVariable('--color-success-hover') || '#34d399',
        dark: '#047857',
        contrastText: '#ffffff',
      },
      background: {
        default: getCSSVariable('--bg-primary') || '#ffffff',
        paper: getCSSVariable('--bg-secondary') || '#f9fafb',
      },
      text: {
        primary: getCSSVariable('--text-primary') || '#111827',
        secondary: getCSSVariable('--text-secondary') || '#6b7280',
        disabled: getCSSVariable('--text-disabled') || '#d1d5db',
      },
      divider: getCSSVariable('--border-color') || '#e5e7eb',
    },
    typography: {
      fontFamily: getCSSVariable('--font-family') || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none', // No uppercase transformation
        fontWeight: 500,
      },
    },
    spacing: 8, // Base spacing unit (8px)
    shape: {
      borderRadius: parseInt(getCSSVariable('--radius-md')) || 6,
    },
    shadows: [
      'none',
      getCSSVariable('--shadow-sm') || '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      getCSSVariable('--shadow-md') || '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      getCSSVariable('--shadow-lg') || '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      getCSSVariable('--shadow-xl') || '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      getCSSVariable('--shadow-2xl') || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      // Repeat last shadow for remaining levels (MUI requires 25 levels)
      ...Array(19).fill(getCSSVariable('--shadow-2xl') || '0 25px 50px -12px rgba(0, 0, 0, 0.25)'),
    ] as any,
    transitions: {
      duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    zIndex: {
      mobileStepper: 1000,
      fab: 1050,
      speedDial: 1050,
      appBar: 1100,
      drawer: 1200,
      modal: parseInt(getCSSVariable('--z-modal')) || 1300,
      snackbar: 1400,
      tooltip: parseInt(getCSSVariable('--z-tooltip')) || 1500,
    },
    components: {
      // Button component overrides
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: getCSSVariable('--radius-md') || '6px',
            padding: '8px 16px',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: getCSSVariable('--transition-normal') || '0.2s ease',
            '&:hover': {
              boxShadow: getCSSVariable('--shadow-sm'),
            },
          },
          sizeSmall: {
            padding: '4px 12px',
            fontSize: '0.8125rem',
          },
          sizeLarge: {
            padding: '12px 24px',
            fontSize: '0.9375rem',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: getCSSVariable('--shadow-sm'),
            },
          },
        },
      },
      // Dialog component overrides
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: getCSSVariable('--radius-lg') || '8px',
            boxShadow: getCSSVariable('--shadow-xl'),
            backgroundImage: 'none',
          },
        },
      },
      // Dialog Title overrides
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.25rem',
            fontWeight: 600,
            padding: '20px 24px',
          },
        },
      },
      // Dialog Content overrides
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '20px 24px',
          },
        },
      },
      // Dialog Actions overrides
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            gap: '8px',
          },
        },
      },
      // TextField component overrides
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: getCSSVariable('--radius-md') || '6px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: getCSSVariable('--border-hover'),
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: getCSSVariable('--border-focus'),
                borderWidth: '2px',
              },
            },
          },
        },
      },
      // Snackbar component overrides (for toasts)
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiSnackbarContent-root': {
              borderRadius: getCSSVariable('--radius-md') || '6px',
              boxShadow: getCSSVariable('--shadow-lg'),
            },
          },
        },
      },
      // Alert component overrides
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: getCSSVariable('--radius-md') || '6px',
            padding: '12px 16px',
          },
          standardSuccess: {
            backgroundColor: getCSSVariable('--color-success'),
            color: '#ffffff',
          },
          standardError: {
            backgroundColor: getCSSVariable('--color-error'),
            color: '#ffffff',
          },
          standardWarning: {
            backgroundColor: getCSSVariable('--color-warning'),
            color: '#ffffff',
          },
          standardInfo: {
            backgroundColor: getCSSVariable('--color-info'),
            color: '#ffffff',
          },
        },
      },
      // Linear Progress component overrides
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: getCSSVariable('--radius-full') || '9999px',
            height: '8px',
          },
        },
      },
      // Circular Progress component overrides
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: getCSSVariable('--color-primary'),
          },
        },
      },
      // Card component overrides
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: getCSSVariable('--radius-lg') || '8px',
            boxShadow: getCSSVariable('--shadow-md'),
            backgroundImage: 'none',
          },
        },
      },
      // Paper component overrides
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: getCSSVariable('--radius-md') || '6px',
          },
          elevation1: {
            boxShadow: getCSSVariable('--shadow-sm'),
          },
          elevation2: {
            boxShadow: getCSSVariable('--shadow-md'),
          },
          elevation3: {
            boxShadow: getCSSVariable('--shadow-lg'),
          },
        },
      },
      // Chip component overrides
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: getCSSVariable('--radius-full') || '9999px',
          },
        },
      },
      // Tooltip component overrides
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: getCSSVariable('--bg-tertiary') || '#374151',
            fontSize: '0.8125rem',
            padding: '6px 12px',
            borderRadius: getCSSVariable('--radius-sm') || '4px',
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

/**
 * Update MUI theme when Skinnr theme changes
 * Call this when theme mode changes to refresh CSS variable values
 */
export const updateMuiTheme = (mode: SkinnrThemeMode): Theme => {
  return createSkinnrMuiTheme(mode);
};
